import { useEffect, useRef, useState, useCallback } from "react";
import { usePlayerContext } from "../providers/PlayerProvider";
import { t } from "../i18n.js";

function formatTime(s) {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

const CATEGORY_COVERS = [
  "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1550745165-9bc0b252726f?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1518709268805-4e9042af9f23?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1443884200821-1a07e74d1a0d?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=400&h=400&fit=crop",
  "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=400&fit=crop",
];

function getCoverUrl(index) {
  return CATEGORY_COVERS[index % CATEGORY_COVERS.length];
}

function VinylRecord({ playing, coverUrl }) {
  return (
    <div className="relative flex-shrink-0" style={{ width: 240, height: 240 }}>
      {playing && (
        <div
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)",
            filter: "blur(8px)",
          }}
        />
      )}
      <div
        className="absolute inset-0 rounded-full bg-gradient-to-br from-gray-700 via-gray-900 to-black border-4 border-gray-600"
        style={{
          animation: playing ? "spin 3s linear infinite" : "none",
          animationPlayState: playing ? "running" : "paused",
          boxShadow: "0 0 40px rgba(0,0,0,0.8), inset 0 0 30px rgba(255,255,255,0.05)",
        }}
      >
        <div className="absolute inset-3 rounded-full border border-gray-500/20" />
        <div className="absolute inset-7 rounded-full border border-gray-500/15" />
        <div className="absolute inset-11 rounded-full border border-gray-500/10" />
        <div className="absolute inset-14 rounded-full overflow-hidden border-2 border-gray-600">
          <img src={coverUrl} alt="cover" className="w-full h-full object-cover" draggable={false} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-5 h-5 rounded-full bg-gray-800 border-2 border-gray-500" />
        </div>
      </div>
    </div>
  );
}

/** 播放列表来源名称 */
function sourceLabel(src, lang) {
  switch (src) {
    case "explore": return t(lang, 'exploreSource');
    case "liked": return t(lang, 'likedSource');
    case "favorites": return t(lang, 'favoritesSource');
    case "playlist": return t(lang, 'playlistSource');
    case "temp": return t(lang, 'tempSource');
    default: return src;
  }
}

/**
 * ImmersivePlayer — 沉浸式播放器。
 * 全屏毛玻璃背景 + 黑胶转盘 + 极简控制条 + 滑动切歌。
 * 新增：喜欢/收藏/不喜欢按钮 + 播放列表面板。
 */
export default function ImmersivePlayer({
  likedTracks,
  favoritedTracks,
  onToggleLike,
  onToggleFavorite,
  playlists,
  lang = "zh",
}) {
  const {
    audioRef,
    currentTrack,
    currentScene,
    isPlaying,
    setPlayerMode,
    togglePlay,
    prevTrack,
    nextTrack,
    playlist,
    playlistSource,
    setPlaylistSource,
    setPlaylist,
    handleDislike,
    playTrack,
    handleShare,
  } = usePlayerContext();

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const touchStartY = useRef(null);
  const progressRef = useRef(null);
  const audio = audioRef?.current;

  const [coverIndex] = useState(() => Math.floor(Math.random() * CATEGORY_COVERS.length));
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  // 喜欢/收藏状态
  const isLiked = likedTracks?.has(currentTrack?.id);
  const isFavorited = favoritedTracks?.has(currentTrack?.id);

  // ── 从共享 audio 同步时间 ──
  useEffect(() => {
    if (!audio) return;
    const update = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };
    const onMeta = () => {
      if (audio.duration && isFinite(audio.duration)) setDuration(audio.duration);
    };
    audio.addEventListener("timeupdate", update);
    audio.addEventListener("loadedmetadata", onMeta);
    update();
    return () => {
      audio.removeEventListener("timeupdate", update);
      audio.removeEventListener("loadedmetadata", onMeta);
    };
  }, [audio, currentTrack?.id]);

  // ── 滑动切歌 ──
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const delta = touchStartY.current - e.changedTouches[0].clientY;
    touchStartY.current = null;
    if (Math.abs(delta) < 60) return;
    if (delta > 0) nextTrack();
    else prevTrack();
  };

  // ── 进度条跳转 ──
  const handleSeek = useCallback(
    (e) => {
      if (!progressRef.current || !audio) return;
      const rect = progressRef.current.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      if (duration > 0) audio.currentTime = ratio * duration;
    },
    [audio, duration]
  );

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bgImage = currentScene?.image || getCoverUrl(coverIndex);

  // ── 不喜欢（标记 + 自动切歌）──
  const handleDislikeClick = () => {
    if (currentTrack) {
      handleDislike?.(currentTrack);
      nextTrack();
    }
  };

  // ── 切换播放列表来源 ──
  const switchSource = (src, playlistId) => {
    if (playlistId) {
      // 用户自定义歌单：通过 /api/playlists/{id} 获取（含 tracks），需带 auth
      const token = localStorage.getItem("fm_token");
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      fetch(`/api/playlists/${playlistId}`, { headers })
        .then((r) => {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then((data) => {
          const items = data.tracks || [];
          // PlaylistTrackOut: [{ track_id, position, track: { id, name_zh, url, ... } }]
          const tracks = items.map((item) => item.track).filter(Boolean);
          setPlaylist(tracks);
          setPlaylistSource("playlist"); // 等 fetch 成功后再切换 source
          if (tracks.length > 0) {
            playTrack(tracks[0], currentScene || null);
          }
        })
        .catch(() => {});
    } else {
      setPlaylistSource(src);
    }
    setShowSourcePicker(false);
  };

  // 当前列表名
  const currentSourceLabel = sourceLabel(playlistSource, lang);

  // ── 渲染播放列表面板 ──
  const renderPlaylistPanel = () => {
    if (!showPlaylist) return null;
    return (
      <div className="absolute inset-0 z-20 flex flex-col" style={{ pointerEvents: "auto" }}>
        {/* 点击背景关闭 */}
        <div className="flex-1" onClick={() => setShowPlaylist(false)} />
        {/* 面板 */}
        <div
          className="bg-gray-900/80 backdrop-blur-xl rounded-t-3xl max-h-[70vh] flex flex-col"
          style={{ animation: "slideUp 0.3s ease-out" }}
        >
          <style>{`
            @keyframes slideUp {
              from { transform: translateY(100%); }
              to { transform: translateY(0); }
            }
          `}</style>

          {/* 面板头部 */}
          <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-white/10 shrink-0">
            <div>
              <h3 className="text-white text-base font-semibold">{t(lang, 'playlist')}</h3>
              <button
                onClick={() => setShowSourcePicker(!showSourcePicker)}
                className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 mt-0.5"
              >
                {currentSourceLabel}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     className={`transition-transform ${showSourcePicker ? "rotate-180" : ""}`}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
            </div>
            <button
              onClick={() => setShowPlaylist(false)}
              className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/50 hover:text-white"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 来源选择器 */}
          {showSourcePicker && (
            <div className="px-4 py-2 border-b border-white/10 shrink-0 space-y-1">
              {[
                { key: "explore", label: "🎯 " + t(lang, 'exploreSource') },
                { key: "liked", label: "❤️ " + t(lang, 'likedSource') },
                { key: "favorites", label: "⭐ " + t(lang, 'favoritesSource') },
                ...playlists.map((p) => ({
                  key: `playlist:${p.id}`,
                  label: `📋 ${p.name}`,
                  origKey: "playlist",
                  playlistId: p.id,
                })),
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() =>
                    switchSource(
                      item.origKey === "playlist"
                        ? "playlist"
                        : item.key,
                      item.origKey === "playlist" ? item.playlistId : undefined
                    )
                  }
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                    playlistSource === item.key || (item.origKey === "playlist" && playlistSource === "playlist")
                      ? "bg-white/15 text-white"
                      : "text-white/60 hover:bg-white/10 hover:text-white/80"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {/* 列表 */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
            {playlist.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-8">{t(lang, 'playlistEmpty')}</p>
            ) : (
              playlist.map((track, idx) => {
                const isCurrent = track.id === currentTrack?.id;
                return (
                  <button
                    key={track.id || idx}
                    onClick={() => {
                      playTrack(track, currentScene || null);
                      setShowPlaylist(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition ${
                      isCurrent
                        ? "bg-white/15 text-white"
                        : "text-white/60 hover:bg-white/10 hover:text-white/80"
                    }`}
                  >
                    <span className="text-xs font-mono w-5 text-right shrink-0 text-white/30">
                      {idx + 1}
                    </span>
                    <div className="flex-1 text-left min-w-0">
                      <div className={`text-sm truncate ${isCurrent ? "font-semibold" : ""}`}>
                        {track.name_zh || track.name_en || track.name || "未知曲目"}
                        {isCurrent && <span className="ml-1.5 text-indigo-400">· {t(lang, 'nowPlaying')}</span>}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col select-none overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        background: `url(${bgImage}) center/cover no-repeat`,
      }}
    >
      {/* 毛玻璃叠层 */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-2xl" />

      {/* 黑色底层（防止闪烁） */}
      <div className="absolute inset-0 bg-black/40" />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* ── 顶部栏 ── */}
      <div className="relative z-10 flex items-center justify-between px-5 py-4 shrink-0">
        <button
          onClick={() => setPlayerMode("bar")}
          className="text-white/60 hover:text-white transition flex items-center gap-2 text-sm"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {t(lang, 'back')}
        </button>

        <div className="flex items-center gap-3">
          {/* 全功能 */}
          <button
            onClick={() => setPlayerMode("fullscreen")}
            className="text-xs text-white/50 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition"
          >
            {t(lang, 'fullFeature')}
          </button>
          {/* 关闭 */}
          <button
            onClick={() => setPlayerMode("bar")}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/60 hover:text-white transition"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9" />
              <polyline points="9 21 3 21 3 15" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── 中心内容 ── */}
      <div className="relative z-10 flex-1 flex flex-col justify-center items-center px-8">
        <VinylRecord playing={isPlaying} coverUrl={getCoverUrl(coverIndex)} />

        <div className="text-center mt-8 mb-6 w-full max-w-xs">
          <h2 className="text-white text-lg font-semibold leading-tight mb-1 truncate">
            {currentTrack?.name_zh || currentTrack?.name_en || currentTrack?.name || "未知曲目"}
          </h2>
          <p className="text-white/40 text-sm">
            {currentScene?.emoji} {currentScene?.name || "场景音乐"}
          </p>
        </div>

        {/* ── 动作按钮：喜欢 / 收藏 / 不喜欢 ── */}
        <div className="flex items-center justify-center gap-8 mb-5">
          {/* 喜欢 */}
          <button
            onClick={() => onToggleLike?.(currentTrack?.id, currentTrack, currentScene)}
            className="flex flex-col items-center gap-1 group"
          >
            <svg
              width="22" height="22"
              viewBox="0 0 24 24"
              className="transition-all"
            >
              {isLiked ? (
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  fill="#ef4444"
                  stroke="#ef4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ) : (
                <path
                  d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-white/50 group-hover:text-red-400"
                />
              )}
            </svg>
            <span className={`text-[10px] ${isLiked ? "text-red-400" : "text-white/40"}`}>{t(lang, 'like')}</span>
          </button>

          {/* 收藏 */}
          <button
            onClick={() => onToggleFavorite?.(currentTrack?.id, currentTrack, currentScene)}
            className="flex flex-col items-center gap-1 group"
          >
            {isFavorited ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#eab308" stroke="#eab308" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round"
                   className="text-white/50 group-hover:text-yellow-400">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            )}
            <span className={`text-[10px] ${isFavorited ? "text-yellow-400" : "text-white/40"}`}>{t(lang, 'favorite')}</span>
          </button>

          {/* 不喜欢 */}
          <button
            onClick={handleDislikeClick}
            className="flex flex-col items-center gap-1 group"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="text-white/50 group-hover:text-orange-400">
              <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
            </svg>
            <span className="text-[10px] text-white/40">{t(lang, 'dislike')}</span>
          </button>

          {/* 分享 */}
          <button
            onClick={() => handleShare(currentTrack)}
            className="flex flex-col items-center gap-1 group"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round"
                 className="text-white/50 group-hover:text-indigo-400">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <span className="text-[10px] text-white/40">{t(lang, 'share')}</span>
          </button>
        </div>

        {/* 进度条 */}
        <div className="w-full max-w-xs mb-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50 font-mono w-10 text-right">{formatTime(currentTime)}</span>
            <div
              ref={progressRef}
              className="flex-1 h-1 bg-white/20 rounded-full cursor-pointer relative group"
              onClick={handleSeek}
            >
              <div
                className="absolute left-0 top-0 h-full bg-white rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow"
                style={{ left: `calc(${progress}% - 6px)` }}
              />
            </div>
            <span className="text-xs text-white/50 font-mono w-10">{formatTime(duration)}</span>
          </div>
        </div>

        {/* 控制按钮 */}
        <div className="flex items-center justify-center gap-10">
          <button onClick={prevTrack} className="text-white/60 hover:text-white transition p-2">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6V6zm3.5 6 8.5 6V6l-8.5 6z" />
            </svg>
          </button>

          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-gray-900 hover:scale-105 active:scale-95 transition-all shadow-xl"
          >
            {isPlaying ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button onClick={nextTrack} className="text-white/60 hover:text-white transition p-2">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zm10-12v12h2V6h-2z" />
            </svg>
          </button>
        </div>
      </div>

      {/* ── 底部：播放列表按钮 + 提示 ── */}
      <div className="relative z-10 shrink-0 flex items-center justify-center gap-4 py-3">
        <button
          onClick={() => setShowPlaylist(!showPlaylist)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white text-xs transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 17l-5-5 5-5" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="15" y1="17" x2="21" y2="17" />
            <line x1="15" y1="7" x2="21" y2="7" />
          </svg>
          {playlist.length > 0 ? `${playlist.length} 首` : "播放列表"}
        </button>
        <span className="text-white/25 text-xs">滑动切换 · 沉浸聆听</span>
      </div>

      {/* ── 播放列表面板 ── */}
      {renderPlaylistPanel()}
    </div>
  );
}
